import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Copy, KeyRound, Trash2, UserPlus, Users } from "lucide-react";

import { api, ApiError, type InviteOut, type Me, type Person, type Role } from "../../api";
import { Modal } from "../../components/Modal";
import { Select } from "../../components/Select";
import { useRole } from "../../hooks/useRole";
import { EmptyState, Note, SettingsSection } from "./shared";

const CUSTOMER_ROLES: { value: Role; label: string }[] = [
  { value: "owner", label: "Owner" },
  { value: "viewer", label: "Viewer" },
];
const STAFF_ROLES: { value: Role; label: string }[] = [
  { value: "viewer", label: "Viewer" },
  { value: "admin", label: "Admin" },
  { value: "dev", label: "Developer" },
];
const STAFF_RANK: Record<string, number> = { viewer: 1, admin: 2, dev: 3 };

const STAFF_ACCOUNT = "staff";

type AddDraft = {
  account: string;
  method: "invite" | "password";
  username: string;
  password: string;
  email: string;
  role: Role;
};

/** Who can sign in. Owners manage their own account; staff manage everyone. */
export function PeopleSection() {
  const qc = useQueryClient();
  const { isStaff, role: myRole } = useRole();
  const me = useQuery<Me>({ queryKey: ["me"], queryFn: api.me });
  const people = useQuery({ queryKey: ["people"], queryFn: api.people.list });
  const customers = useQuery({
    queryKey: ["customers"],
    queryFn: api.customers.list,
    enabled: isStaff,
  });
  const [adding, setAdding] = useState<AddDraft | null>(null);
  const [resetting, setResetting] = useState<{ person: Person; password: string } | null>(null);
  const [removing, setRemoving] = useState<Person | null>(null);
  const [invited, setInvited] = useState<(InviteOut & { email: string }) | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = () => {
    setError(null);
    qc.invalidateQueries({ queryKey: ["people"] });
    qc.invalidateQueries({ queryKey: ["customers"] });
  };
  const fail = (fallback: string) => (e: unknown) =>
    setError(e instanceof ApiError ? e.message : fallback);

  const staffRoles = STAFF_ROLES.filter((r) => STAFF_RANK[r.value] <= (STAFF_RANK[myRole] ?? 0));
  const rolesFor = (customerId: number | null) => (customerId == null ? staffRoles : CUSTOMER_ROLES);
  const roleLabel = (p: Person) =>
    [...(p.customer_id == null ? STAFF_ROLES : CUSTOMER_ROLES)].find((r) => r.value === p.role)
      ?.label ?? p.role;

  const add = useMutation({
    mutationFn: async (d: AddDraft) => {
      const account = isStaff
        ? { customer_id: d.account === STAFF_ACCOUNT ? null : Number(d.account) }
        : {};
      if (d.method === "invite") {
        const out = await api.people.invite({ role: d.role, email: d.email.trim(), ...account });
        return { ...out, email: d.email.trim() };
      }
      await api.people.create({
        username: d.username.trim(),
        password: d.password,
        role: d.role,
        ...(d.email.trim() ? { email: d.email.trim() } : {}),
        ...account,
      });
      return null;
    },
    onSuccess: (out) => {
      setAdding(null);
      if (out) setInvited(out);
      refresh();
    },
    onError: fail("Could not add the person."),
  });
  const changeRole = useMutation({
    mutationFn: ({ id, role }: { id: number; role: Role }) => api.people.update(id, { role }),
    onSuccess: refresh,
    onError: fail("Could not change the role."),
  });
  const reset = useMutation({
    mutationFn: ({ person, password }: { person: Person; password: string }) =>
      api.people.update(person.id, { password }),
    onSuccess: () => {
      setResetting(null);
      refresh();
    },
    onError: fail("Could not set the password."),
  });
  const remove = useMutation({
    mutationFn: (p: Person) => api.people.delete(p.id),
    onSuccess: () => {
      setRemoving(null);
      refresh();
    },
    onError: (e: unknown) => {
      setRemoving(null);
      fail("Could not remove the person.")(e);
    },
  });

  const groups = useMemo(() => {
    const all = people.data ?? [];
    if (!isStaff) return [{ key: "account", title: "", people: all }];
    const byAccount = new Map<string, { key: string; title: string; people: Person[] }>();
    for (const p of all) {
      const key = p.customer_id == null ? STAFF_ACCOUNT : String(p.customer_id);
      const title = p.customer_id == null ? "IntraScene staff" : p.customer_name;
      const g = byAccount.get(key) ?? { key, title, people: [] };
      g.people.push(p);
      byAccount.set(key, g);
    }
    return [...byAccount.values()].sort((a, b) =>
      a.key === STAFF_ACCOUNT ? -1 : b.key === STAFF_ACCOUNT ? 1 : a.title.localeCompare(b.title),
    );
  }, [people.data, isStaff]);

  const accountOptions = [
    { value: STAFF_ACCOUNT, label: "IntraScene staff" },
    ...(customers.data ?? []).map((c) => ({ value: String(c.id), label: c.name })),
  ];
  const startAdding = () =>
    setAdding({
      account: isStaff ? STAFF_ACCOUNT : "",
      method: "invite",
      username: "",
      password: "",
      email: "",
      role: "viewer",
    });
  const addRoles = !isStaff || adding?.account !== STAFF_ACCOUNT ? CUSTOMER_ROLES : staffRoles;

  return (
    <SettingsSection
      title="People"
      description={
        isStaff
          ? "Everyone who can sign in: IntraScene staff and each customer's people."
          : "Who can sign in to your account."
      }
      hint={
        isStaff ? (
          <>
            Staff viewers see everything; admins also manage customers, boxes
            and settings; developers also see operations. A customer's owners
            manage their own account; its viewers watch, search, play back and
            see alerts.
          </>
        ) : (
          <>
            <b>Owners</b> manage locations, cameras, video recorders, boxes and
            people. <b>Viewers</b> can watch, search, play back and see alerts.
          </>
        )
      }
      actions={
        <button className="bf-button" onClick={startAdding}>
          <UserPlus size={16} className="mr-2" /> Add person
        </button>
      }
    >
      {error && <Note tone="error">{error}</Note>}

      {people.isLoading ? (
        <div className="bf-card text-sm text-bf-muted">Loading people…</div>
      ) : (people.data ?? []).length === 0 ? (
        <EmptyState icon={Users} title="No one else can sign in yet" />
      ) : (
        groups.map((g) => (
          <div key={g.key} className="space-y-2">
            {g.title && (
              <h3 className="text-xs font-semibold uppercase tracking-wide text-bf-muted">
                {g.title}
              </h3>
            )}
            <div className="bf-card overflow-x-auto p-0">
              <table className="w-full text-sm">
                <thead className="text-left text-xs uppercase tracking-wide text-bf-muted">
                  <tr>
                    <th className="p-3">Username</th>
                    <th className="p-3">Role</th>
                    <th className="p-3">Added</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {g.people.map((p) => {
                    const self = p.username === me.data?.username;
                    const options = rolesFor(p.customer_id);
                    const editable =
                      !self && options.some((o) => o.value === p.role);
                    return (
                      <tr key={p.id} className="border-t border-white/5">
                        <td className="p-3 font-medium">
                          {p.username}
                          {self && <span className="ml-2 text-xs text-bf-muted">(you)</span>}
                        </td>
                        <td className="p-3">
                          {editable ? (
                            <Select
                              className="w-36"
                              ariaLabel={`Role of ${p.username}`}
                              value={p.role}
                              disabled={changeRole.isPending}
                              onChange={(v) => changeRole.mutate({ id: p.id, role: v as Role })}
                              options={options}
                            />
                          ) : (
                            <span className="text-bf-muted">{roleLabel(p)}</span>
                          )}
                        </td>
                        <td className="p-3 text-xs text-bf-muted">
                          {new Date(p.created_at).toLocaleDateString()}
                        </td>
                        <td className="p-3 text-right">
                          {editable && (
                            <div className="flex justify-end gap-3">
                              <button
                                className="text-bf-muted hover:text-bf-text"
                                aria-label={`Set a new password for ${p.username}`}
                                title="Set a new password"
                                onClick={() => setResetting({ person: p, password: "" })}
                              >
                                <KeyRound size={15} />
                              </button>
                              <button
                                className="text-bf-muted hover:text-red-400"
                                aria-label={`Remove ${p.username}`}
                                title="Remove"
                                onClick={() => setRemoving(p)}
                              >
                                <Trash2 size={15} />
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ))
      )}

      {adding && (
        <Modal onClose={() => setAdding(null)} labelledBy="person-add-title">
          <form
            className="space-y-3"
            onSubmit={(e) => {
              e.preventDefault();
              add.mutate(adding);
            }}
          >
            <h3 id="person-add-title" className="text-lg font-medium text-white">
              Add a person
            </h3>
            {isStaff && (
              <div>
                <span className="mb-1 block text-xs text-bf-muted">Account</span>
                <Select
                  ariaLabel="Account"
                  value={adding.account}
                  onChange={(account) =>
                    setAdding({
                      ...adding,
                      account,
                      role: "viewer",
                    })
                  }
                  options={accountOptions}
                />
              </div>
            )}
            <div className="flex gap-1 rounded-lg bg-white/5 p-1 text-sm" role="tablist">
              {(
                [
                  ["invite", "Send an invite link"],
                  ["password", "Set a first password"],
                ] as const
              ).map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  role="tab"
                  aria-selected={adding.method === value}
                  className={
                    "flex-1 rounded-md px-3 py-1.5 transition " +
                    (adding.method === value ? "bg-white/10 text-white" : "text-bf-muted hover:text-bf-text")
                  }
                  onClick={() => setAdding({ ...adding, method: value })}
                >
                  {label}
                </button>
              ))}
            </div>
            {adding.method === "invite" ? (
              <div className="grid grid-cols-2 gap-3">
                <label>
                  <span className="mb-1 block text-xs text-bf-muted">Email</span>
                  <input
                    className="bf-input"
                    type="email"
                    value={adding.email}
                    onChange={(e) => setAdding({ ...adding, email: e.target.value })}
                    autoComplete="off"
                    autoFocus
                  />
                </label>
                <div>
                  <span className="mb-1 block text-xs text-bf-muted">Role</span>
                  <Select
                    ariaLabel="Role"
                    value={adding.role}
                    onChange={(v) => setAdding({ ...adding, role: v as Role })}
                    options={addRoles}
                  />
                </div>
                <span className="col-span-2 text-xs text-bf-muted">
                  They choose their own username and password from the link, which works
                  once, for 7 days.
                </span>
              </div>
            ) : (
            <>
            <div className="grid grid-cols-2 gap-3">
              <label>
                <span className="mb-1 block text-xs text-bf-muted">Username</span>
                <input
                  className="bf-input"
                  value={adding.username}
                  onChange={(e) => setAdding({ ...adding, username: e.target.value })}
                  pattern="[A-Za-z0-9._@+\-]{2,64}"
                  title="Letters, numbers and . _ @ + -"
                  autoComplete="off"
                  required
                  autoFocus
                />
              </label>
              <div>
                <span className="mb-1 block text-xs text-bf-muted">Role</span>
                <Select
                  ariaLabel="Role"
                  value={adding.role}
                  onChange={(v) => setAdding({ ...adding, role: v as Role })}
                  options={addRoles}
                />
              </div>
            </div>
            <label className="block">
              <span className="mb-1 block text-xs text-bf-muted">First password</span>
              <input
                className="bf-input"
                type="password"
                value={adding.password}
                onChange={(e) => setAdding({ ...adding, password: e.target.value })}
                minLength={8}
                autoComplete="new-password"
                required
              />
              <span className="mt-1 block text-xs text-bf-muted">
                At least 8 characters. They can change it after signing in.
              </span>
            </label>
            <label className="block">
              <span className="mb-1 block text-xs text-bf-muted">Email (for password resets)</span>
              <input
                className="bf-input"
                type="email"
                value={adding.email}
                onChange={(e) => setAdding({ ...adding, email: e.target.value })}
                autoComplete="off"
              />
            </label>
            </>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                className="bf-button bg-white/10 bg-none"
                onClick={() => setAdding(null)}
              >
                Cancel
              </button>
              <button className="bf-button disabled:opacity-40" type="submit" disabled={add.isPending}>
                {add.isPending
                  ? "Saving…"
                  : adding.method === "invite"
                    ? "Create invite"
                    : "Add person"}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {invited && (
        <Modal onClose={() => setInvited(null)} panelClassName="w-full max-w-lg">
          <div className="space-y-3">
            <h3 className="text-lg font-medium text-white">Invite ready</h3>
            <p className="text-sm text-bf-muted">
              {invited.emailed
                ? `Emailed to ${invited.email}. You can also send this link yourself.`
                : "Send this link to the person. It works once, for 7 days."}
            </p>
            <div className="flex gap-2">
              <input className="bf-input font-mono text-xs" readOnly value={invited.link} />
              <button
                type="button"
                className="bf-button shrink-0"
                aria-label="Copy link"
                onClick={() => void navigator.clipboard?.writeText(invited.link)}
              >
                <Copy size={16} />
              </button>
            </div>
            <div className="flex justify-end">
              <button className="bf-button bg-white/10 bg-none" onClick={() => setInvited(null)}>
                Done
              </button>
            </div>
          </div>
        </Modal>
      )}

      {resetting && (
        <Modal onClose={() => setResetting(null)} labelledBy="person-reset-title">
          <form
            className="space-y-3"
            onSubmit={(e) => {
              e.preventDefault();
              reset.mutate(resetting);
            }}
          >
            <h3 id="person-reset-title" className="text-lg font-medium text-white">
              New password for {resetting.person.username}
            </h3>
            <input
              className="bf-input"
              type="password"
              value={resetting.password}
              onChange={(e) => setResetting({ ...resetting, password: e.target.value })}
              minLength={8}
              autoComplete="new-password"
              aria-label="New password"
              required
              autoFocus
            />
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                className="bf-button bg-white/10 bg-none"
                onClick={() => setResetting(null)}
              >
                Cancel
              </button>
              <button className="bf-button disabled:opacity-40" type="submit" disabled={reset.isPending}>
                {reset.isPending ? "Saving…" : "Set password"}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {removing && (
        <Modal onClose={() => setRemoving(null)} panelClassName="w-full max-w-md">
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-white">Remove {removing.username}?</h3>
            <p className="text-sm text-bf-muted">They can no longer sign in.</p>
            <div className="flex justify-end gap-2">
              <button className="bf-button bg-white/10 bg-none" onClick={() => setRemoving(null)}>
                Cancel
              </button>
              <button
                className="bf-button bg-red-600 bg-none hover:bg-red-500 disabled:opacity-40"
                disabled={remove.isPending}
                onClick={() => remove.mutate(removing)}
              >
                {remove.isPending ? "Removing…" : "Remove"}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </SettingsSection>
  );
}
